/**
 * @author      Abdul Pathan
 * @date        Aug, 2024
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  ToggleButtonGroup,
  ToggleButton,
} from 'react-bootstrap';
import SparkApi from '../../../api/SparkApi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment-timezone';
import { useNavigate, useLocation } from 'react-router-dom';
import { MultiSelect } from 'react-multi-select-component';
import { saveAs } from 'file-saver';
import TemplateModal from '../whatsapp_messenger/TemplateModal';
import DataApi from '../../../api/DataApi';
import WatconnectApi from '../../../api/WatconnectApi';

const CampaignAdd = ({ selectedWhatsAppSetting, userInfo }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const existingData = location.state || '';

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [leadOptions, setLeadOptions] = useState([]);
  const [campaignRecord, setCampaignRecord] = useState({
    name: '',
    description: '',
    schedule_date: '',
    type: 'Web',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSpinner, setIsSpinner] = useState(true);
  const [fileMembers, setFileMembers] = useState([]);
  const [finalTotalCost, setFinalTotalCost] = useState(0);
  const [templateRates, setTemplateRates] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  const fileInputRef = useRef(null);
  const [recurrenceType, setRecurrenceType] = useState('');
  const [customRecurrenceUnit, setCustomRecurrenceUnit] = useState('week');
  const [repeatEvery, setRepeatEvery] = useState(1);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [recurrenceEndType, setRecurrenceEndType] = useState('never');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [existingFileInfo, setExistingFileInfo] = useState(null);
  const [templateSubmittedData, setTemplateSubmittedData] = useState(null);

  const isEditCloneMode = Boolean(
    existingData && existingData?.type === 'clone'
  );

  useEffect(() => {
    fetchAllData();
  }, [selectedWhatsAppSetting]);

  useEffect(() => {
    if (isEditCloneMode && existingData) {
      setCampaignRecord({
        name: existingData?.campaign_name
          ? `${existingData.campaign_name} clone`
          : '',
        description: existingData?.file_description || '',
        schedule_date: new Date(),
        type: existingData?.campaign_type || 'Web',
      });

      if (existingData?.file_id) {
        setExistingFileInfo({
          id: existingData?.file_id,
          title: existingData?.file_title,
          type: existingData?.file_type,
          size: existingData?.file_size,
          description: existingData?.file_description,
        });
      }

      if (existingData?.lead_ids?.length > 0) {
        const matchedLeads = leadOptions.filter(option =>
          existingData.lead_ids.some(
            lead => lead.member_id === option.value.member_id
          )
        );
        setSelectedLeads(matchedLeads);
      }
    }
  }, [isEditCloneMode, existingData, leadOptions]);

  const fetchAllData = async () => {
    try {
      const dataApi = new DataApi('watconnect_history');

      let leadsResult = await dataApi.fetchAll(
        `filter=lead&search=${encodeURIComponent('')}&businessNumber=${encodeURIComponent(selectedWhatsAppSetting || '')}`
      );
      console.log(leadsResult);
      // const leadsResult = await SparkApi.getFilterData('', 'lead');
      if (leadsResult.data.success) {
        const leadOptions = leadsResult.data.records.map(lead => ({
          label: `${lead.contactname} ${lead.country_code ? lead.country_code : ''}${lead.whatsapp_number}`,
          value: {
            member_id: lead.id,
            name: lead.contactname,
            whatsapp_number: `${lead.country_code ? lead.country_code : ''}${lead.whatsapp_number}`,
            recordtypename: 'lead',
          },
        }));
        setLeadOptions(leadOptions);
      }
    } catch (error) {
      toast.error('Error fetching leads.');
    }
  };

  const handleLeadChange = selectedOption => {
    setSelectedLeads(selectedOption);
  };

  const handleChangeName = event => {
    const { name, value } = event.target;
    if (name === 'schedule_date') {
      setCampaignRecord({
        ...campaignRecord,
        [name]: value ? moment(value).tz('Asia/Kolkata').toDate() : '',
      });
    } else {
      setCampaignRecord({ ...campaignRecord, [name]: value });
    }
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (file) {
      const validExtensions = ['csv'];
      const fileExtension = file.name.split('.').pop().toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setErrorMessage('Only .csv files are allowed.');
        setSelectedFile(null);
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = event => {
        const content = event.target.result;
        const rows = content
          .split('\n')
          .map(row => row.trim())
          .filter(Boolean);

        if (rows.length === 0) {
          setErrorMessage('The file is empty.');
          setSelectedFile(null);
          return;
        }

        const headers = rows[0]
          .split(',')
          .map(header => header.trim().toLowerCase());
        const expectedHeaders = ['name', 'countrycode', 'number'];

        const isHeaderValid = expectedHeaders.every(
          (h, index) => headers[index] === h
        );
        if (!isHeaderValid) {
          setErrorMessage(
            'File must contain headers in order: Name, Country Code, Number.'
          );
          setSelectedFile(null);
          event.target.value = '';
          return;
        }

        const members = rows.slice(1).map(row => {
          const [name, countrycode, number] = row
            .split(',')
            .map(val => val.trim());
          return { name, countrycode, number };
        });

        setFileMembers(members);
        setSelectedFile([file]);
        setExistingFileInfo(null);
        setErrorMessage('');
      };
      reader.readAsText(file);
    }
  };

  // inside your CampaignAdd.jsx or CampaignAdd.js

  const handleTemplateSend = submittedData => {
    console.log('📩 Template submitted data received:', submittedData);
    setShowTemplateModal(false);
    setTemplateSubmittedData(submittedData); // store temporarily
    setSelectedTemplate(submittedData.selectedTemplate);
  };
  // main campaign submit handler
  const handleSubmit = async () => {
    try {
      const errors = {};

      // Validation
      if (!campaignRecord.name) {
        errors.name = 'Campaign name is required.';
      }
      if (!campaignRecord?.schedule_date) {
        errors.schedule_date = 'Schedule date & time is required.';
      }

      // Validate that at least one of leads or file is provided
      if (selectedLeads.length === 0 && !selectedFile && !existingFileInfo) {
        errors.recipients = 'Either select leads or upload a file.';
      }

      if (
        recurrenceType === 'custom' &&
        customRecurrenceUnit === 'week' &&
        selectedDays.length === 0
      ) {
        errors.recurrence =
          'Please select at least one day for weekly recurrence.';
      }

      if (
        recurrenceType === 'custom' &&
        customRecurrenceUnit === 'month' &&
        !selectedDate
      ) {
        errors.recurrence = 'Please select a date for monthly recurrence.';
      }

      if (recurrenceEndType === 'on' && !recurrenceEndDate) {
        errors.recurrenceEndDate =
          'Please select an end date for the recurrence.';
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      setFormErrors({});

      if (!templateSubmittedData) {
        alert(
          'Please select a template and fill parameters before submitting.'
        );
        return;
      }

      const {
        selectedTemplate,
        selectedWhatsAppSetting: templateWhatsAppSetting,
        fileUploads,
        parameterValues,
      } = templateSubmittedData;

      if (!selectedTemplate) {
        errors.template = 'Template is required.';
      }

      const campaignApi = new DataApi('watconnect');
      const attachmentsApi = new DataApi('attachments');

      setIsSending(true);

      // STEP 1️⃣: CREATE CAMPAIGN FIRST
      const leadIds = selectedLeads.map(lead => lead.value.member_id);

      const formattedStartDate = campaignRecord.schedule_date
        ? moment(campaignRecord.schedule_date)
            .tz('Asia/Kolkata')
            .format('YYYY-MM-DDTHH:mm:ss.SSSZ')
        : moment
            .tz(new Date(), 'Asia/Kolkata')
            .format('YYYY-MM-DDTHH:mm:ss.SSSZ');

      // STEP 2️⃣: CREATE CAMPAIGN (actual API call)
      // Convert arrays to PostgreSQL format or omit empty arrays
      const campaignData = {
        name: campaignRecord.name,
        type: campaignRecord.type || 'Web',
        status: 'Pending',
        template_name: selectedTemplate.name,
        business_number: selectedWhatsAppSetting,
        schedule_date: formattedStartDate,
        recurrence_end_type: recurrenceEndType,
        parameters: parameterValues,
      };

      // Only include lead_ids if not empty, or format for PostgreSQL
      if (leadIds.length > 0) {
        campaignData.lead_ids = leadIds;
      } else {
        // For PostgreSQL, try sending as empty PostgreSQL array format
        campaignData.lead_ids = '{}'; // PostgreSQL empty array format
      }

      // Handle recurrence fields - only include if they have values
      if (recurrenceType) {
        campaignData.recurrence = recurrenceType;
      }

      if (recurrenceType === 'custom' && customRecurrenceUnit) {
        campaignData.custom_recurrence_unit = customRecurrenceUnit;
      }

      // Handle selected_days - only include if applicable and not empty
      if (customRecurrenceUnit === 'week') {
        if (selectedDays.length > 0) {
          campaignData.selected_days = selectedDays;
        } else {
          campaignData.selected_days = '{}'; // PostgreSQL empty array format
        }
      }

      // Handle selected_date - only include if applicable
      if (customRecurrenceUnit === 'month' && selectedDate) {
        campaignData.selected_date = selectedDate;
      }

      // Handle recurrence end date
      if (recurrenceEndType === 'on' && recurrenceEndDate) {
        campaignData.recurrence_end_date =
          moment(recurrenceEndDate).format('YYYY-MM-DD');
      }

      console.log('Campaign Data:', campaignData);

      // ✅ CREATE CAMPAIGN FIRST
      const createRes = await campaignApi.post(`/campaign`, campaignData);
      console.log('API Response:', createRes);

      if (!createRes?.data?.success)
        throw new Error('Campaign creation failed');

      const campaignId = createRes.data.data.id;
      console.log('✅ Campaign created:', campaignId);

      // STEP 3️⃣: UPLOAD MAIN CAMPAIGN FILE (if any) - AFTER CAMPAIGN CREATION
      if (selectedFile) {
        console.log('📤 Uploading campaign file...');
        const fileForm = new FormData();
        for (let i = 0; i < selectedFile.length; i++) {
          fileForm.append('files', selectedFile[i]);
        }
        fileForm.append(
          'description',
          `${campaignRecord.name} file for WatConnect`
        );

        const attachmentRes = await attachmentsApi.post(
          `/${campaignId}`,
          fileForm
        );
        console.log('File upload response:', attachmentRes);
      }

      toast.success('Campaign created successfully!');
      navigate('/watconnect/campaigns');
    } catch (error) {
      console.error('❌ Error in campaign submission:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleClear = () => {
    setSelectedTemplate(null);
    setCampaignRecord({
      name: '',
      description: '',
      schedule_date: '',
      type: 'Web',
    });
    setSelectedFile(null);
    setSelectedLeads([]);
    setFileMembers([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBack = () => {
    navigate('/watconnect/campaigns');
  };

  const formatDateForInput = date => {
    return date ? moment(date).format('YYYY-MM-DDTHH:mm') : '';
  };

  const csvData = [
    ['Name', 'CountryCode', 'Number'],
    ['John', '91', 'XXXXXXXXXX'],
  ];

  const convertToCSV = array => {
    return array.map(row => row.join(',')).join('\n');
  };

  const downloadCSV = () => {
    const csvString = convertToCSV(csvData);
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'contacts.csv');
  };

  const handleDayToggle = day => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  if (!isSpinner) {
    return (
      <div className='sk-cube-grid'>
        <div className='sk-cube sk-cube1'></div>
        <div className='sk-cube sk-cube2'></div>
        <div className='sk-cube sk-cube3'></div>
        <div className='sk-cube sk-cube4'></div>
        <div className='sk-cube sk-cube5'></div>
        <div className='sk-cube sk-cube6'></div>
        <div className='sk-cube sk-cube7'></div>
        <div className='sk-cube sk-cube8'></div>
        <div className='sk-cube sk-cube9'></div>
      </div>
    );
  }

  return (
    <>
      <Container className='mt-5'>
        <Row className='mx-5 text-center g-0'>
          <Col lg={12} xs={12} sm={12}>
            <div
              className='text-center p-2'
              style={{
                height: '40px',
                backgroundColor: '#ffffff',
                borderRadius: '5px',
              }}
            >
              <span
                className='fw-semibold p-1'
                style={{ color: '#605C68', fontSize: 'large' }}
              >
                Add Campaign
              </span>
            </div>
          </Col>
        </Row>
      </Container>

      <Container className='mt-1 mb-5'>
        <Row className='mx-5 g-0'>
          <Col lg={12} sm={12} xs={12} className='mb-2'>
            <Card className='h-100' style={{ border: 'none' }}>
              <Card.Body>
                <Row className='mb-1'>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group
                      className='mb-3 mx-2'
                      controlId='formCampaignName'
                    >
                      <Form.Label>Campaign Name</Form.Label>
                      <Form.Control
                        style={{ height: '36px' }}
                        required
                        type='text'
                        name='name'
                        placeholder='Enter campaign name'
                        value={campaignRecord?.name}
                        onChange={handleChangeName}
                        isInvalid={!!formErrors.name}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {formErrors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className='mb-3 mx-2' controlId='formStartDate'>
                      <Form.Label>Schedule Date & Time</Form.Label>
                      <Form.Control
                        style={{ height: '36px' }}
                        required
                        type='datetime-local'
                        name='schedule_date'
                        value={formatDateForInput(
                          campaignRecord?.schedule_date
                        )}
                        onChange={handleChangeName}
                        isInvalid={!!formErrors.schedule_date}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {formErrors.schedule_date}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='mb-1'>
                  <Col lg={6} sm={12}>
                    <Form.Group
                      className='mb-3 mx-2'
                      controlId='formSelectLeads'
                    >
                      <Form.Label>Select Leads</Form.Label>
                      <MultiSelect
                        options={leadOptions}
                        value={selectedLeads}
                        onChange={handleLeadChange}
                        labelledBy='Select Leads'
                      />
                      {formErrors.recipients && (
                        <Form.Text className='text-danger'>
                          {formErrors.recipients}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className='mb-3 mx-2'>
                      <Form.Label htmlFor='formType'>Type</Form.Label>
                      <Form.Select
                        className='w-100'
                        style={{ height: '42px' }}
                        aria-label='select type'
                        name='type'
                        value={campaignRecord?.type}
                        onChange={handleChangeName}
                      >
                        <option value='Web'>Web</option>
                        <option value='Advertisement'>Advertisement</option>
                        <option value='Banner Ads'>Banner Ads</option>
                        <option value='Conference'>Conference</option>
                        <option value='Direct Mail'>Direct Mail</option>
                        <option value='Email'>Email</option>
                        <option value='Partners'>Partners</option>
                        <option value='Public Relations'>
                          Public Relations
                        </option>
                        <option value='Other'>Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='mb-1'>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className='mb-3 mx-2' controlId='formFile'>
                      <Form.Label>
                        {existingFileInfo ? (
                          <>
                            Existing file:&nbsp;
                            <a
                              href={`/public/${userInfo.tenantcode}/campaign_files/${existingData.file_title}`}
                              download={existingData.file_title}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              {existingFileInfo.title} ({existingFileInfo.size}{' '}
                              KB)
                            </a>
                          </>
                        ) : (
                          'File Upload'
                        )}
                      </Form.Label>
                      <Form.Control
                        type='file'
                        ref={fileInputRef}
                        accept='.csv'
                        onChange={handleFileChange}
                      />
                      {errorMessage && (
                        <Form.Text className='text-danger'>
                          {errorMessage}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className='mb-3 mx-2'>
                      <Form.Label htmlFor='formType'>
                        <strong className='text-danger'>*</strong> Select
                        Template
                      </Form.Label>
                      <Button
                        className='w-100 btn-group text-capitalize'
                        onClick={() => setShowTemplateModal(true)}
                        variant={
                          formErrors.template
                            ? 'outline-danger'
                            : 'outline-primary'
                        }
                      >
                        {selectedTemplate?.name?.replace(/_/g, ' ') ||
                          'Select Template...'}
                      </Button>
                      {formErrors.template && (
                        <Form.Text className='text-danger'>
                          {formErrors.template}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row className='mb-1'>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group
                      className='mb-3 mx-2'
                      controlId='formCampaignName'
                    >
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        style={{ height: '36px' }}
                        type='text'
                        name='description'
                        placeholder='Type description here...'
                        value={campaignRecord?.description}
                        onChange={handleChangeName}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Recurrence Section - Keep your existing recurrence UI */}
                <Row
                  lg={12}
                  sm={12}
                  xs={12}
                  className='py-3 my-4 section-header'
                >
                  CUSTOM RECURRENCE
                </Row>

                <Row className='mb-1'>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className='mb-3 mx-2'>
                      <Form.Label>Repeat</Form.Label>
                      <Form.Select
                        value={recurrenceType}
                        onChange={e => setRecurrenceType(e.target.value)}
                      >
                        <option value=''>Does not repeat</option>
                        <option value='daily'>Daily</option>
                        <option value='weekly'>Weekly</option>
                        <option value='monthly'>Monthly</option>
                        <option value='fourthmonthly'>
                          Monthly on the fourth {moment().format('dddd')}
                        </option>
                        <option value='custom'>Custom</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {recurrenceType && recurrenceType !== '' && (
                    <>
                      <Col lg={2} sm={6} xs={6}>
                        <Form.Group className='mb-3 mx-2'>
                          <Form.Label>Repeat Ends</Form.Label>
                          <ToggleButtonGroup
                            type='radio'
                            name='recurrence-end'
                            value={recurrenceEndType}
                            onChange={val => setRecurrenceEndType(val)}
                            className='d-flex'
                          >
                            <ToggleButton
                              id='recurrence-end-never'
                              value='never'
                              variant={
                                recurrenceEndType === 'never'
                                  ? 'primary'
                                  : 'outline-primary'
                              }
                            >
                              Never
                            </ToggleButton>
                            <ToggleButton
                              id='recurrence-end-on'
                              value='on'
                              variant={
                                recurrenceEndType === 'on'
                                  ? 'primary'
                                  : 'outline-primary'
                              }
                            >
                              On
                            </ToggleButton>
                          </ToggleButtonGroup>{' '}
                        </Form.Group>
                      </Col>
                      {recurrenceEndType === 'on' && (
                        <Col lg={2} sm={6} xs={6}>
                          <Form.Group className='mb-3 mx-2'>
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                              style={{ height: '36px' }}
                              type='date'
                              min={moment().add(1, 'days').format('YYYY-MM-DD')}
                              value={recurrenceEndDate}
                              onChange={e =>
                                setRecurrenceEndDate(e.target.value)
                              }
                            />
                          </Form.Group>
                        </Col>
                      )}
                    </>
                  )}
                </Row>

                {recurrenceType === 'custom' && (
                  <Row className='mb-1'>
                    <Col lg={6} sm={12} xs={12}>
                      <Form.Group className='mb-3 mx-2'>
                        <Form.Label>Repeat Every</Form.Label>
                        <Form.Select
                          value={customRecurrenceUnit}
                          onChange={e =>
                            setCustomRecurrenceUnit(e.target.value)
                          }
                        >
                          <option value='week'>Week(s)</option>
                          <option value='month'>Month(s)</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    {customRecurrenceUnit === 'week' && (
                      <Col lg={6} sm={12} xs={12}>
                        <Form.Group className='mb-3 mx-2'>
                          <Form.Label className='mb-3'>
                            Repeat On (Days of Week)
                          </Form.Label>
                          <div className='d-flex flex-wrap'>
                            {[
                              'Sunday',
                              'Monday',
                              'Tuesday',
                              'Wednesday',
                              'Thursday',
                              'Friday',
                              'Saturday',
                            ].map(day => (
                              <Form.Check
                                key={day}
                                type='checkbox'
                                label={day}
                                value={day}
                                checked={selectedDays.includes(day)}
                                onChange={() => handleDayToggle(day)}
                                className='me-3'
                              />
                            ))}
                          </div>
                        </Form.Group>
                      </Col>
                    )}

                    {customRecurrenceUnit === 'month' && (
                      <Col lg={4} sm={12} xs={12}>
                        <Form.Group className='mb-3 mx-2'>
                          <Form.Label>Repeat On (Date of Month)</Form.Label>
                          <Form.Select
                            value={selectedDate}
                            onChange={e => setSelectedDate(e.target.value)}
                          >
                            <option value=''>Select Date</option>
                            {[...Array(31)].map((_, i) => (
                              <option key={i + 1} value={i + 1}>
                                {i + 1}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                )}

                <Row className='mt-3'>
                  <Col lg={12} sm={12} xs={12}>
                    <hr />
                  </Col>
                </Row>

                <Row className='g-0 mb-2'>
                  <Col lg={3} sm={3} xs={3} className='text-start mt-2'>
                    <Button
                      className='ms-2'
                      variant='secondary'
                      onClick={downloadCSV}
                    >
                      <i className='fa fa-download me-2'></i>Download Sample CSV
                    </Button>
                  </Col>
                  <Col lg={9} sm={9} xs={9} className='text-end mt-2'>
                    <b>
                      Approximate Total Cost{' '}
                      <span className='text-success'>{finalTotalCost}</span>
                    </b>

                    <Button
                      className='mx-2'
                      variant='light'
                      onClick={handleBack}
                      disabled={isSending}
                    >
                      Back
                    </Button>
                    <Button
                      className='mx-2'
                      variant='light'
                      onClick={handleClear}
                      disabled={isSending}
                    >
                      Clear
                    </Button>
                    <Button
                      variant='primary'
                      disabled={isSending}
                      onClick={handleSubmit}
                    >
                      {isSending ? 'Submitting...' : 'Submit'}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {showTemplateModal && (
          <TemplateModal
            isOpen={showTemplateModal}
            onClose={() => setShowTemplateModal(false)}
            selectedWhatsAppSetting={selectedWhatsAppSetting}
            onSendComplete={handleTemplateSend}
          />
        )}
      </Container>

      <ToastContainer />
    </>
  );
};

export default CampaignAdd;
